"use client";

/**
 * DialIQ — Threshold Toast
 *
 * Appears over the dialer when a lead hits the 5x no-answer threshold and
 * DialIQ moves it to the next window, or takes it out of redial after 3 full
 * rounds. Non-blocking: the agent can keep the lead where it was or skip it
 * for today. Leaves after 8 seconds; hover or keyboard focus holds it open.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { X } from "lucide-react";
import redialService from "@/app/_module/services/redialService";
import {
  WINDOW_META,
  type RedialWindow,
} from "@/app/_module/services/redialService.types";
import { successToast, errorToast } from "@/app/_module/services/helper";
import { Button } from "@/app/_module/components/ui/button";
import { cn } from "@/app/_module/lib/utils";
import { focusRing } from "@/app/(authenticated)/settings/a2p-verification/_components/overview/primitives";
import { DialIQMark } from "@/app/(authenticated)/leads/_modules/components/DialIQMark";

const AUTO_DISMISS_MS = 8000;
const RESUME_MS = 3000;
const LEAVE_MS = 200;

// ────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────

export interface ThresholdEvent {
  id: string; // unique per event for React key
  leadId: string;
  leadName: string | null;
  leadState: string | null;
  fromWindow: RedialWindow;
  toWindow: RedialWindow | null; // null if terminal
  terminal: boolean;
  thresholdCount?: number; // default 5
}

interface RedialThresholdToastProps {
  event: ThresholdEvent | null;
  onDismiss: () => void;
  /** Absolute positioning class. Default top-right corner of parent. */
  positionClass?: string;
}

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

export function RedialThresholdToast({
  event,
  onDismiss,
  positionClass = "absolute right-4 top-4 z-50",
}: RedialThresholdToastProps) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const triggerLeave = useCallback(() => {
    setLeaving(true);
    setTimeout(() => {
      setVisible(false);
      onDismiss();
    }, LEAVE_MS);
  }, [onDismiss]);

  // Trigger enter animation on mount of new event
  useEffect(() => {
    if (!event) return;
    setLeaving(false);
    // Tiny delay so CSS transition engages
    const raf = requestAnimationFrame(() => setVisible(true));
    timerRef.current = setTimeout(triggerLeave, AUTO_DISMISS_MS);

    return () => {
      cancelAnimationFrame(raf);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.id]);

  const hold = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const release = () => {
    if (!leaving && !timerRef.current) timerRef.current = setTimeout(triggerLeave, RESUME_MS);
  };

  const undoMutation = useMutation({
    mutationFn: () => redialService.undoAdvance(event!.leadId, event!.fromWindow),
    onSuccess: () => {
      successToast(
        `Kept in ${WINDOW_META[event!.fromWindow].label.toLowerCase()}. DialIQ won't move this lead again.`,
      );
      triggerLeave();
    },
    onError: () => errorToast("Couldn't keep the lead there. Try again."),
  });

  const skipMutation = useMutation({
    mutationFn: () => redialService.skipForToday(event!.leadId),
    onSuccess: () => {
      successToast("Removed from today's redial queue");
      triggerLeave();
    },
    onError: () => errorToast("Couldn't update the lead"),
  });

  if (!event || !visible) return null;

  const from = WINDOW_META[event.fromWindow].label.toLowerCase();
  const to = event.toWindow ? WINDOW_META[event.toWindow] : null;
  const name = event.leadName || "This lead";
  const threshold = event.thresholdCount ?? 5;

  return (
    <div
      role="status"
      aria-live="polite"
      onMouseEnter={hold}
      onMouseLeave={release}
      onFocus={hold}
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) release();
      }}
      className={cn(
        positionClass,
        "pointer-events-auto w-[min(360px,calc(100vw-2rem))] transition-[opacity,transform] duration-200 ease-out",
        leaving ? "-translate-y-1 opacity-0" : "translate-y-0 opacity-100",
      )}
    >
      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-lg">
        <div className="flex items-start gap-3">
          <DialIQMark
            size={18}
            className={cn("mt-0.5 shrink-0", event.terminal ? "text-rose-600" : "text-primary")}
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900">
              {event.terminal
                ? `${name} marked unreachable`
                : `${name} moved to ${to ? to.label.toLowerCase() : "the next window"}`}
            </p>
            <p className="mt-0.5 text-sm text-gray-600">
              {event.terminal
                ? "No answer in any window after 3 full rounds. DialIQ took the lead out of redial and tagged it Unreachable."
                : // NBSP keeps "30 days" and "OH time" from splitting across lines
                  `${threshold} unanswered ${from} calls in 30\u00A0days. Now queued for ${
                    to ? `${to.timeLabel}, ${event.leadState ?? "their"}\u00A0time` : "the next window"
                  }.`}
            </p>
          </div>
          <button
            type="button"
            onClick={triggerLeave}
            aria-label="Dismiss"
            className={cn(
              "-mr-1.5 -mt-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800",
              focusRing,
            )}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {!event.terminal && (
          <div className="mt-3 flex flex-wrap gap-2 pl-[30px]">
            <Button
              size="sm"
              variant="outline"
              onClick={() => undoMutation.mutate()}
              disabled={undoMutation.isPending || skipMutation.isPending}
              className={focusRing}
            >
              {undoMutation.isPending ? "Keeping…" : `Keep in ${from}`}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => skipMutation.mutate()}
              disabled={undoMutation.isPending || skipMutation.isPending}
              className={cn("text-gray-700", focusRing)}
            >
              {skipMutation.isPending ? "Skipping…" : "Skip today"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default RedialThresholdToast;
